
/**
 * ELAKAY – GraffitiPreview.jsx
 *
 * Composant partagé qui regroupe :
 *   - DesignsList         (liste paginée des designs)
 *   - DesignPreviewModal_  (preview détaillée d'un design)
 *   - ConfirmDeleteModal  (confirmation de suppression)
 *
 * À importer dans :
 *   - SellerDashboard.jsx
 *   - GraffitiOrderClient.jsx
 *   - GraffitiOrderSeller.jsx
 *
 * Props :
 *   - productID    (string, requis)  - ID du produit concerné
 *   - authToken    (string)          - token d'auth (requis si mode="seller")
 *   - mode         ("seller"|"public", défaut "seller")
 *   - title        (string)          - titre du header (défaut "My Designs")
 *   - subtitle     (string)          - sous-titre du header
 *   - showHeader   (bool, défaut true)
 *   - showCreateButton (bool, défaut true)
 *   - onCreateNew  (fn)              - callback bouton "Create new"
 *   - onLoadDesign (fn)              - callback bouton "Edit" zzZz
 *   - extraActions (fn(design) => React) - boutons d'action custom par carte
 *
 * Dépendances internes : SlotPreview_, DesignCanvas, parseLoadedDesign,
 * getImageUrl, FONT_STYLES, useDesignsList.
 *
 * Le composant gère son propre état previewDesign en interne.
 */


import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {DesignCanvas} from "./DesignStudio"
import { createPortal } from "react-dom";
import { elakay_API, toImageUrl } from "../../../../config";
import { elakayAutoText } from "../../dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["graffitiPreview"]?.[key] || fallback;

/* ══════════════════════════════════════════════════════

   CONSTANTES de rendu (partagées avec DesignStudio)
══════════════════════════════════════════════════════ */
const FONT_STYLES = [
  { id: "impact",   label: __elakayText("t095", "Block"),  css: "'Impact','Arial Black',sans-serif" },
  { id: "bebas",    label: __elakayText("t096", "Bebas"),  css: "'Bebas Neue',sans-serif" },
  { id: "georgia",  label: __elakayText("t093", "Serif"),  css: "Georgia,serif" },
  { id: "courier",  label: __elakayText("t097", "Mono"),   css: "'Courier New',monospace" },
  { id: "treb",     label: __elakayText("t098", "Sans"),   css: "'Trebuchet MS',sans-serif" },
  { id: "palatino", label: __elakayText("t099", "Italic"), css: "'Palatino Linotype',serif" },
];

/* ══════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════ */
const getImageUrl = (obj) => {
  if (!obj) return "";
  // localUrl is a blob:/data: preview of a not-yet-uploaded file — never
  // rebase those. The rest come from the server and may carry a stale host
  // baked in from an older migration/env; toImageUrl is a no-op for
  // anything that isn't one of our own /elakay_api/ URLs.
  if (obj.localUrl && typeof obj.localUrl === "string" && obj.localUrl.trim() !== "") return obj.localUrl;
  const candidates = [obj.urlImg, obj.url, obj.src];
  for (const c of candidates) {
    if (c && typeof c === "string" && c.trim() !== "") return toImageUrl(c);
  }
  return "";
};

const parseLoadedDesign = (serverDesign) => {
  if (!serverDesign) return null;

  const slots = (serverDesign.slots || []).map((s, i) => ({
    id:       s.id    || `s_${Date.now()}_${i}`,
    label:    s.label || `Face ${i + 1}`,
    url:      s.url   || "",
    localUrl: "",
    fileName: "",
    file:     null
  }));

  const designs = (serverDesign.slots || []).map(s => {
    const ss = s.slotState || {};
    return {
      texts: (ss.texts || []).map(tb => ({
        role:           tb.role         || "Text",
        content:        tb.content      || "",
        fontId:         tb.fontId       || "impact",
        size:           typeof tb.size === "number"          ? tb.size          : 56,
        color:          tb.color        || "#c9a227",
        outlineColor:   tb.outlineColor || "#000000",
        outlineWidth:   typeof tb.outlineWidth === "number"  ? tb.outlineWidth  : 2,
        shadowEnabled:  !!tb.shadowEnabled,
        shadowColor:    tb.shadowColor  || "#000000",
        gradient:       !!tb.gradient,
        gradColor:      tb.gradColor    || "#007399",
        textTransform:  tb.textTransform || "uppercase",
        letterSpacing:  typeof tb.letterSpacing === "number" ? tb.letterSpacing : 0,
        rotation:       typeof tb.rotation === "number"      ? tb.rotation      : 0,
        opacity:        typeof tb.opacity === "number"       ? tb.opacity       : 100,
        pos: {
          x: tb.pos?.x ?? 50,
          y: tb.pos?.y ?? 35
        }
      })),
      layers: (ss.layers || []).map(l => ({
        _id:      l._id || null,
        urlImg:   l.urlImg || "",
        src:      l.urlImg || "",
        url:      l.urlImg || "",
        file:     null,
        imgEl:    null,
        size:     typeof l.size === "number"     ? l.size     : 25,
        rotation: typeof l.rotation === "number" ? l.rotation : 0,
        opacity:  typeof l.opacity === "number"  ? l.opacity  : 100,
        pos: {
          x: l.pos?.x ?? 50,
          y: l.pos?.y ?? 50
        }
      })),
      instructions: ss.instructions || "",
      zoom: 1,
      pan:  { x: 0, y: 0 }
    };
  });

  const templateMeta = {
    label:  serverDesign.label  || "Untitled",
    style:  serverDesign.style  || "Street Art",
    detail: serverDesign.detail || "Moderate"
  };

  return {
    designId:   serverDesign._id,
    status:     serverDesign.status,
    productID:  typeof serverDesign.productID === "object"
                  ? serverDesign.productID._id
                  : serverDesign.productID,
    createdAt:  serverDesign.createdAt,
    updatedAt:  serverDesign.updatedAt,
    usageCount: serverDesign.usageCount || 0,
    slots,
    designs,
    templateMeta
  };
};

/* ══════════════════════════════════════════════════════
   JsonHighlight — Coloration syntaxique simple du JSON
══════════════════════════════════════════════════════ */
function JsonHighlight({ json }) {
  const formatted = JSON.stringify(json, null, 2);
  const html = formatted
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/"([^"]+)":/g,'<span class="jk">"$1"</span>:')
    .replace(/: "([^"]*)"/g,': <span class="js">"$1"</span>')
    .replace(/: (\d+\.?\d*)/g,': <span class="jn">$1</span>')
    .replace(/: (true|false|null)/g,': <span class="jv">$1</span>');
  return <pre className="sd-json-pre" dangerouslySetInnerHTML={{__html:html}}/>;
}

/* ══════════════════════════════════════════════════════
   FaceDetail — Détail complet d'une face (étape 3 ou Preview)
   Inclut un grand aperçu visuel + détails textes/layers
══════════════════════════════════════════════════════ */
function FaceDetail({ slot, designSlot, index }) {
  /* Si c'est appelé depuis OutputView, designSlot vient de slot.slotState */
  const data = designSlot || slot.slotState;
  const texts  = data?.texts || [];
  const layers = data?.layers || [];
  const instructions = data?.instructions || "";

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Grand aperçu de la face avec layers + textes */}
      <div style={{
        background: "var(--n3)",
        borderRadius: "var(--r)",
        border: "1px solid var(--b)",
        overflow: "hidden"
      }}>
        <SlotPreview
          slot={{
            ...slot,
            slotState: { texts, layers, instructions }
          }}
          aspectRatio="3/4"
          showTexts={true}
          showLayers={true}
          dimFactor={0.7}
          showLabel={false}
        />

        {/* Header avec infos */}
        <div style={{
          padding: 12,
          background: "var(--n3)",
          borderTop: "1px solid var(--b)"
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>{__elakayText("t035", "Face #")}{index + 1} — {slot.label}
          </div>
          <div style={{
            fontSize: 9.5, color: "var(--muted)",
            fontFamily: "var(--ffm)",
            wordBreak: "break-all", opacity: 0.8
          }}>{__elakayText("t036", "ID:")}{slot.id}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <Pill icon="✏️" text={`${texts.length} text${texts.length > 1 ? "s" : ""}`} color="var(--gold)" />
            <Pill icon="🖼️" text={`${layers.length} image${layers.length > 1 ? "s" : ""}`} color="var(--teal)" />
          </div>
        </div>
      </div>

      {/* Bloc Textes */}
      {texts.length > 0 && (
        <DetailSection title={__elakayText("t037", "Text blocks")} icon="✏️" count={texts.length}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {texts.map((tb, i) => (
              <TextBlockRow key={i} tb={tb} index={i} />
            ))}
          </div>
        </DetailSection>
      )}

      {/* Bloc Layers */}
      {layers.length > 0 && (
        <DetailSection title={__elakayText("t038", "Image overlays")} icon="🖼️" count={layers.length}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {layers.map((layer, i) => (
              <LayerRow key={i} layer={layer} index={i} />
            ))}
          </div>
        </DetailSection>
      )}

      {/* Instructions */}
      {instructions && (
        <DetailSection title={__elakayText("t011", "Instructions")} icon="📝">
          <div style={{
            padding: "10px 12px",
            background: "rgba(0,115,153,.06)",
            border: "1px solid rgba(0,115,153,.2)",
            borderRadius: "var(--r)",
            fontSize: 11, color: "var(--gold2)",
            lineHeight: 1.5, fontStyle: "italic"
          }}>
            "{instructions}"
          </div>
        </DetailSection>
      )}

      {/* État vide */}
      {texts.length === 0 && layers.length === 0 && !instructions && (
        <div style={{
          padding: 30, textAlign: "center",
          color: "var(--muted)", fontSize: 11, fontStyle: "italic",
          background: "var(--n3)",
          border: "1px dashed var(--b2)",
          borderRadius: "var(--r)"
        }}>{__elakayText("t039", "No element on this face")}</div>
      )}
    </div>
  );
}

/* Vue JSON brute avec copie */
function RawJsonView({ output }) {
  const [copied, setCopied] = useState(false);

  const copyJson = async () => {
    const text = `// ── ${output.label} ──\n` + JSON.stringify(output, null, 2) + ",";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:10,color:"var(--muted)"}}>{__elakayText("t040", "Raw data for")}<code style={{color:"var(--gold)"}}>EXISTING_GRAPHICS</code>
        </span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {copied && <span style={{color:"var(--ok)",fontSize:10,fontWeight:600}}>{__elakayText("t041", "✓ Copied!")}</span>}
          <button type="button" className="sd-btn sd-btn-teal"
            style={{padding:"5px 12px",fontSize:10}} onClick={copyJson}>{__elakayText("t042", "📋 Copy")}</button>
        </div>
      </div>
      <JsonHighlight json={output} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   COMPOSANTS AUXILIAIRES (pour OutputView)
══════════════════════════════════════════════════════ */

/* Carte de stat */
function StatCard({ icon, value, label, color = "var(--gold)", badge = null }) {
  return (
    <div style={{
      background:"var(--n3)",border:"1px solid var(--b)",borderRadius:"var(--r)",
      padding:"14px 16px",display:"flex",flexDirection:"column",gap:4
    }}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:16}}>{icon}</span>
        {badge && (
          <span style={{
            fontSize:9,padding:"2px 7px",borderRadius:10,
            background:"var(--gold)",color:"#fff",fontWeight:700,fontFamily:"var(--ffm)"
          }}>{badge}</span>
        )}
      </div>
      <div style={{fontFamily:"var(--ffm)",fontSize:24,fontWeight:700,color,lineHeight:1}}>
        {value}
      </div>
      <div style={{fontSize:10,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.7}}>
        {label}
      </div>
    </div>
  );
}

/* Item de méta */
function MetaItem({ label, value, highlight = false, mono = false }) {
  return (
    <div style={{
      background:"var(--n3)",border:"1px solid var(--b)",borderRadius:"var(--r)",
      padding:"10px 12px",display:"flex",flexDirection:"column",gap:3
    }}>
      <span style={{fontSize:9,color:"var(--muted)",textTransform:"uppercase",letterSpacing:0.8,fontWeight:600}}>
        {label}
      </span>
      <span style={{
        fontSize:highlight?14:12,
        color:highlight?"var(--gold)":"var(--white)",
        fontWeight:highlight?700:500,
        fontFamily:mono?"var(--ffm)":"inherit",
        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
      }}>
        {value || "—"}
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   FacePreviewCard — Carte d'aperçu d'une face dans OutputView
   Tab "Preview" étape 3
══════════════════════════════════════════════════════ */
function FacePreviewCard({ slot, index, onClick }) {
  const newLayers = (slot.slotState?.layers || []).filter(l => l.file instanceof File).length;
  const textCount = slot.slotState?.texts?.length || 0;
  const layerCount = slot.slotState?.layers?.length || 0;

  return (
    <div onClick={onClick}
      style={{
        background: "var(--n3)",
        borderRadius: "var(--r)",
        overflow: "hidden",
        border: "1px solid var(--b2)",
        cursor: "pointer",
        transition: "all .15s",
        display: "flex",
        flexDirection: "column"
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "var(--gold)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "var(--b2)";
        e.currentTarget.style.transform = "translateY(0)";
      }}>

      {/* Aperçu avec layers + textes via SlotPreview_ */}
      <div style={{ position: "relative" }}>
        <SlotPreview
          slot={slot}
          aspectRatio="3/4"
          showTexts={true}
          showLayers={true}
          dimFactor={0.65}
        />

        {/* Numéro de face */}
        <div style={{
          position: "absolute", top: 8, left: 8, zIndex: 5,
          width: 22, height: 22,
          background: "var(--gold)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--ffm)",
          fontSize: 10, fontWeight: 700,
          color: "#fff"
        }}>
          {index + 1}
        </div>

        {/* Badge nouveaux layers */}
        {newLayers > 0 && (
          <div style={{
            position: "absolute", top: 8, right: 8, zIndex: 5,
            padding: "3px 8px",
            background: "rgba(0,115,153,.95)",
            borderRadius: 10,
            fontSize: 9, fontWeight: 700,
            color: "#fff"
          }}>
            ⬆ {newLayers}{__elakayText("t100", "new")}</div>
        )}
      </div>

      {/* Infos */}
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--white)" }}>
          {slot.label}
        </div>
        <div style={{
          fontSize: 9.5, color: "var(--muted)",
          display: "flex", gap: 8
        }}>
          <span>✏️ {textCount}</span>
          <span>🖼️ {layerCount}</span>
          {slot.slotState?.instructions && <span>📝</span>}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 3 — OUTPUT VIEW
══════════════════════════════════════════════════════ */
function OutputView({
  output,
  payload,
  publishPayload,
  canPublish,
  onNewTemplate,
  onEditBack,
  productID,
  authToken,
  save
}) {
  const [activeTab, setActiveTab] = useState("preview");
  const [activeFaceIdx, setActiveFaceIdx] = useState(0);

  /* Stats calculées */
  const stats = useMemo(() => {
    const slots = output.slots || [];
    return {
      faces: slots.length,
      texts: slots.reduce((a, s) => a + (s.slotState?.texts?.length || 0), 0),
      layers: slots.reduce((a, s) => a + (s.slotState?.layers?.length || 0), 0),
      newLayers: slots.reduce((a, s) =>
        a + (s.slotState?.layers || []).filter(l => l.file instanceof File).length, 0),
      withInstructions: slots.filter(s => s.slotState?.instructions?.length > 0).length,
    };
  }, [output]);

  const isPublished = save.savedStatus === "published";
  const isDraft = save.savedStatus === "draft";
  const activeFace = output.slots[activeFaceIdx];

  const formatTime = (date) => {
    if (!date) return "—";
    return date.toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", second:"2-digit" });
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Bandeau de statut principal */}
      <div style={{
        background: isPublished
          ? "linear-gradient(135deg, rgba(52,199,89,.10), rgba(52,199,89,.04))"
          : isDraft
            ? "linear-gradient(135deg, rgba(0,180,216,.10), rgba(0,180,216,.04))"
            : "linear-gradient(135deg, rgba(0,115,153,.08), rgba(0,115,153,.03))",
        border:`1px solid ${isPublished ? "rgba(52,199,89,.3)" : isDraft ? "rgba(0,180,216,.3)" : "rgba(0,115,153,.25)"}`,
        borderRadius:"var(--rL)",padding:"16px 20px",
        display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"
      }}>
        <div style={{display:"flex",alignItems:"center",gap:14,flex:1,minWidth:240}}>
          <div style={{
            width:48,height:48,borderRadius:12,
            background:isPublished ? "var(--emerald)" : isDraft ? "var(--teal)" : "var(--gold)",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:22,flexShrink:0,
            boxShadow:`0 4px 14px ${isPublished ? "rgba(52,199,89,.35)" : isDraft ? "rgba(0,180,216,.35)" : "rgba(0,115,153,.28)"}`
          }}>
            {isPublished ? "✅" : isDraft ? "💾" : "📋"}
          </div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"var(--white)",marginBottom:2}}>
              {output.label || elakayAutoText("h_621521f9a87886", "Untitled")}
            </div>
            <div style={{fontSize:11,color:"var(--muted)",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{
                padding:"2px 8px",borderRadius:12,
                background:isPublished ? "rgba(52,199,89,.15)" : isDraft ? "rgba(0,180,216,.15)" : "rgba(0,115,153,.15)",
                color:isPublished ? "var(--ok)" : isDraft ? "var(--teal)" : "var(--gold)",
                fontWeight:600,fontSize:9.5,textTransform:"uppercase",letterSpacing:0.6
              }}>
                {isPublished ? elakayAutoText("h_2f463c6dd593c4", "● Published") : isDraft ? elakayAutoText("h_8ebf0902e70e23", "● Draft") : elakayAutoText("h_44d5d410f0a2f6", "○ Not saved")}
              </span>
              {save.savedAt && (
                <span>{__elakayText("t043", "Last update:")}<strong style={{color:"var(--white)"}}>{formatTime(save.savedAt)}</strong></span>
              )}
              {save.designId && (
                <span style={{fontFamily:"var(--ffm)",fontSize:9}}>{__elakayText("t036", "ID:")}{save.designId.slice(-8)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Boutons d'action principaux */}
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <button type="button" className="sd-btn sd-btn-sec"
            onClick={save.saveDraft} disabled={save.busy}
            style={{minWidth:140}}>
            {save.busyMode === "draft" ? <>{__elakayText("t044", "⏳ Saving...")}</> : <>{__elakayText("t045", "💾 Save Draft")}</>}
          </button>
          <button type="button" className="sd-btn sd-btn-emerald"
            onClick={save.publish} disabled={save.busy}
            style={{minWidth:140}}>
            {save.busyMode === "publish"
              ? <>{__elakayText("t046", "⏳ Publishing...")}</>
              : isPublished ? <>{__elakayText("t047", "🔄 Republish")}</> : <>{__elakayText("t048", "🚀 Publish")}</>
            }
          </button>
        </div>
      </div>

      {/* Affichage d'erreur */}
      {save.error && (
        <div style={{
          background:"rgba(239,68,68,.06)",
          border:"1px solid rgba(239,68,68,.25)",
          borderRadius:"var(--r)",padding:"12px 16px",
          display:"flex",alignItems:"flex-start",gap:12
        }}>
          <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
          <div style={{flex:1}}>
            <div style={{color:"var(--err)",fontWeight:700,fontSize:12,marginBottom:4}}>{__elakayText("t049", "Save error")}</div>
            <div style={{color:"var(--err)",fontSize:11,opacity:0.9,lineHeight:1.5}}>
              {save.error.includes("DOCTYPE") || save.error.includes("Unexpected token")
                ? elakayAutoText("h_04a4b9ffcecc72", "The server returned an HTML page instead of JSON. Check the API URL or server configuration (missing route or 500 error).")
                : save.error
              }
            </div>
            {save.error.includes("DOCTYPE") && (
              <div style={{
                marginTop:8,padding:"8px 10px",
                background:"rgba(239,68,68,.05)",borderRadius:6,
                fontSize:10,fontFamily:"var(--ffm)",color:"var(--err)",opacity:0.85
              }}>{__elakayText("t050", "💡 Check:")}<br/>{__elakayText("t051", "· Does the POST")}<code>`${elakay_API}/graphic-design/add-update/:id`</code>{__elakayText("t052", "route exist?")}<br/>{__elakayText("t053", "· Is the dev proxy configured?")}<br/>{__elakayText("t054", "· Is the auth token valid?")}</div>
            )}
          </div>
          <button type="button" onClick={save.clearError}
            style={{
              background:"transparent",border:"none",color:"var(--err)",
              cursor:"pointer",fontSize:16,flexShrink:0,padding:4
            }}>✕</button>
        </div>
      )}

      {/* Stats — 4 cartes */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:10}}>
        <StatCard icon="🎨" value={stats.faces} label={__elakayText("t101", "Faces")} color="var(--gold)" />
        <StatCard icon="✏️" value={stats.texts} label={__elakayText("t037", "Text blocks")} color="var(--teal)" />
        <StatCard icon="🖼️" value={stats.layers} label={__elakayText("t102", "Images")} color="var(--emerald)"
          badge={stats.newLayers > 0 ? `+${stats.newLayers}` : null} />
        <StatCard icon="📝" value={stats.withInstructions} label={__elakayText("t055", "With notes")} color="var(--white)" />
      </div>

      {/* Métadonnées */}
      <div className="sd-card">
        <div className="sd-card-head">
          <span className="sd-card-icon">🏷️</span>
          <span className="sd-card-title">{__elakayText("t056", "Template Information")}</span>
        </div>
        <div className="sd-card-body">
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10}}>
            <MetaItem label={__elakayText("t103", "Name")} value={output.label || "Untitled"} highlight />
            <MetaItem label={__elakayText("t057", "Artistic style")} value={output.style} />
            <MetaItem label={__elakayText("t005", "Detail level")} value={output.detail} />
            {save.designId && <MetaItem label={__elakayText("t058", "MongoDB ID")} value={save.designId} mono />}
          </div>
        </div>
      </div>

      {/* Tabs : Preview / Details / Raw JSON */}
      <div className="sd-card" style={{overflow:"hidden"}}>
        <div style={{display:"flex",borderBottom:"1px solid var(--b)",background:"var(--n2)"}}>
          {[
            { id:"preview", label:__elakayText("t059", "🖼️ Preview"),  count:null },
            { id:"data",    label:__elakayText("t060", "📋 Face details"), count:stats.faces },
            { id:"raw",     label:__elakayText("t061", "{ } JSON"), count:null }
          ].map(tab => (
            <button key={tab.id} type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding:"12px 20px",
                background:activeTab === tab.id ? "var(--n3)" : "transparent",
                border:"none",
                borderBottom:`2px solid ${activeTab === tab.id ? "var(--gold)" : "transparent"}`,
                color:activeTab === tab.id ? "var(--gold)" : "var(--muted)",
                fontSize:12,fontWeight:600,cursor:"pointer",
                fontFamily:"var(--ffb)",
                display:"flex",alignItems:"center",gap:8
              }}>
              {tab.label}
              {tab.count !== null && (
                <span style={{
                  fontSize:9,padding:"1px 7px",borderRadius:10,
                  background:activeTab === tab.id ? "var(--golda)" : "var(--n3)",
                  color:activeTab === tab.id ? "var(--gold)" : "var(--muted)",
                  fontFamily:"var(--ffm)"
                }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab : Preview */}
        {activeTab === "preview" && (
          <div style={{padding:16}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))",gap:12}}>
              {output.slots.map((slot, i) => (
                <FacePreviewCard key={i} slot={slot} index={i}
                  onClick={() => { setActiveFaceIdx(i); setActiveTab("data"); }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Tab : Détails par face */}
        {activeTab === "data" && (
          <div style={{display:"flex",flexDirection:"column"}}>
            <div style={{
              display:"flex",gap:0,padding:"8px 12px",
              borderBottom:"1px solid var(--b)",background:"var(--n2)",
              overflowX:"auto"
            }}>
              {output.slots.map((slot, i) => (
                <button key={i} type="button"
                  onClick={() => setActiveFaceIdx(i)}
                  style={{
                    padding:"6px 14px",
                    background:activeFaceIdx === i ? "var(--golda)" : "transparent",
                    border:"none",borderRadius:6,
                    color:activeFaceIdx === i ? "var(--gold)" : "var(--muted)",
                    fontSize:11,fontWeight:600,cursor:"pointer",
                    fontFamily:"var(--ffb)",whiteSpace:"nowrap",marginRight:4
                  }}>
                  <span style={{marginRight:6}}>{i + 1}</span>{slot.label}
                </button>
              ))}
            </div>
            {activeFace && <FaceDetail slot={activeFace} index={activeFaceIdx} />}
          </div>
        )}

        {/* Tab : JSON brut */}
        {activeTab === "raw" && (
          <div style={{padding:16}}>
            <RawJsonView output={output} />
          </div>
        )}
      </div>

      {/* Footer navigation */}
      <div style={{
        display:"flex",justifyContent:"space-between",alignItems:"center",
        flexWrap:"wrap",gap:10,paddingTop:4
      }}>
        <div style={{display:"flex",gap:8}}>
          <button type="button" className="sd-btn sd-btn-sec" onClick={onEditBack}>{__elakayText("t062", "← Edit design")}</button>
          <button type="button" className="sd-btn sd-btn-gold" onClick={onNewTemplate}>{__elakayText("t063", "+ New template")}</button>
        </div>

        {!save.savedStatus && (
          <div style={{fontSize:10,color:"var(--muted)",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:12}}>💡</span>
            <span>{__elakayText("t064", "Don't forget to save your work")}</span>
          </div>
        )}

        {isDraft && (
          <div style={{fontSize:10,color:"var(--teal)",display:"flex",alignItems:"center",gap:6,fontWeight:600}}>
            <span>💾</span>
            <span>{__elakayText("t065", "Auto-save every 30s")}</span>
          </div>
        )}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════
   HOOK : useDesignsList
══════════════════════════════════════════════════════ */
export function useDesignsList({
  productID,
  authToken,
  mode = "seller"
}) {
  const [designs, setDesigns]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [deleting, setDeleting] = useState(null);

  const safeJsonParse = useCallback(async (res) => {
    const contentType = res.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      const text = await res.text();
      const preview = text.slice(0, 100).replace(/\s+/g, " ");
      if (res.status === 404) throw new Error(`Route not found (404). Check the API URL.`);
      if (res.status === 401 || res.status === 403) throw new Error(`Authentication required (${res.status})`);
      if (res.status >= 500) throw new Error(`Server error (${res.status}). ${preview}`);
      throw new Error(`Non-JSON response (${res.status}). ${preview}...`);
    }

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.message || `HTTP error ${res.status}`);
    return data;
  }, []);

  const buildListUrl = useCallback(() => {
    if (mode === "seller") return `${elakay_API}/graphic-design/seller/product/${productID}`;
    return `${elakay_API}/graphic-design/product/${productID}`;
  }, [productID, mode]);

  const fetchDesigns = useCallback(async () => {
    if (!productID) return;
    if (mode === "seller" && !authToken) {
      setError(elakayAutoText("h_553630866fabfd", "Authentication token required for seller mode"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = buildListUrl();
      const res = await fetch(url, {
        method: "GET",
        headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {}
      });
      const data = await safeJsonParse(res);
      if (!Array.isArray(data)) { setDesigns([]); return; }
      setDesigns(data);
    } catch (err) {
      console.error("fetchDesigns error:", err);
      setError(err.message || elakayAutoText("h_6a42df4a558519", "Failed to load designs"));
      setDesigns([]);
    } finally {
      setLoading(false);
    }
  }, [productID, authToken, mode, buildListUrl, safeJsonParse]);

  const fetchDesignById = useCallback(async (designId) => {
    if (!designId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${elakay_API}/graphic-design/${designId}`, {
        method: "GET",
        headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {}
      });
      const data = await safeJsonParse(res);
      if (!data || !data._id) throw new Error("Invalid design data received from server");
      return data;
    } catch (err) {
      console.error("fetchDesignById error:", err);
      setError(err.message || elakayAutoText("h_bf030c02fc1739", "Failed to load design"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken, safeJsonParse]);

  const deleteDesign = useCallback(async (designId) => {
    if (!designId) return false;
    setDeleting(designId);
    setError(null);
    try {
      const res = await fetch(`${elakay_API}/graphic-design/delete/${designId}`, {
        method: "DELETE",
        headers: authToken ? { "Authorization": `Bearer ${authToken}` } : {}
      });
      const data = await safeJsonParse(res);
      if (!res.ok) throw new Error(data.error || "Failed to delete");
      setDesigns(prev => prev.filter(d => d._id !== designId));
      return true;
    } catch (err) {
      console.error("deleteDesign error:", err);
      setError(err.message || elakayAutoText("h_047d18d5e54ec5", "Failed to delete design"));
      return false;
    } finally {
      setDeleting(null);
    }
  }, [authToken, safeJsonParse]);

  useEffect(() => { fetchDesigns(); }, [fetchDesigns]);

  const clearError = useCallback(() => setError(null), []);

  return {
    designs, loading, error, deleting, mode,
    fetchDesigns, fetchDesignById, deleteDesign, clearError
  };
}
const DEFAULT_PAN = { x: 0, y: 0 };
const EMPTY_ARRAY = [];

/* ══════════════════════════════════════════════════════
   SlotPreview — Aperçu mini d'un slot (canvas)
══════════════════════════════════════════════════════ */
function SlotPreview({
  slot,
  aspectRatio = "3/4",
  showLabel = false,
  dimFactor = 1,
  className = "",
  style = {},
  showControls = false,
  defaultMode = "all",
  downloadFilename = null,
  downloadHD = true,
  zoom = 1,
  pan = DEFAULT_PAN,
  onZoomChange = null,
  enablePanInteraction = true
}) {
  const url = getImageUrl(slot);
  const texts = slot?.slotState?.texts || EMPTY_ARRAY;
  const layers = slot?.slotState?.layers || EMPTY_ARRAY;

  const [mode, setMode] = useState(defaultMode);

  const showBg = mode === "all" || mode === "url";
  const showTexts = mode === "all" || mode === "elements";
  const showLayers = mode === "all" || mode === "elements";

  const canvasRef = useRef(null);
  const stageRef  = useRef(null);
  const posterRef = useRef(null);
  const layerImagesRef = useRef({});
  const [downloading, setDownloading] = useState(false);
  const [tick, setTick] = useState(0);

  const zRef = useRef(zoom);
  const pRef = useRef(pan);
  const panning = useRef(false);
  const panStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

useEffect(() => {
  if (zRef.current !== zoom) {
    zRef.current = zoom;
    setTick(t => t + 1);
  }
}, [zoom]);

useEffect(() => {
  const oldPan = pRef.current || DEFAULT_PAN;
  const newPan = pan || DEFAULT_PAN;

  if (oldPan.x !== newPan.x || oldPan.y !== newPan.y) {
    pRef.current = newPan;
    setTick(t => t + 1);
  }
}, [pan]);

  const aspectValue = useMemo(() => {
    if (typeof aspectRatio === "number") return aspectRatio;
    const parts = String(aspectRatio).split("/");
    if (parts.length === 2) {
      const a = parseFloat(parts[0]);
      const b = parseFloat(parts[1]);
      if (a > 0 && b > 0) return a / b;
    }
    return 0.75;
  }, [aspectRatio]);

  /* Chargement bg */
  useEffect(() => {
    posterRef.current = null;
    if (!url) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { posterRef.current = img; setTick(t => t + 1); };
    img.onerror = () => console.warn("SlotPreview: bg failed:", url);
    img.src = url;
  }, [url]);

  /* Chargement layers */
  useEffect(() => {
    const cache = layerImagesRef.current;
    layers.forEach(layer => {
      const u = getImageUrl(layer);
      if (!u || cache[u]) return;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => { cache[u] = img; setTick(t => t + 1); };
      img.onerror = () => console.warn("SlotPreview: layer failed:", u);
      img.src = u;
    });
  }, [layers]);

  const drawTo = useCallback((ctx, W, H, options = {}) => {
    const {
      forceBg = showBg,
      forceTexts = showTexts,
      forceLayers = showLayers,
      hideLabel = false,
      transparentIfNoBg = false,
      applyTransform = true
    } = options;

    ctx.clearRect(0, 0, W, H);

    if (applyTransform) {
      ctx.save();
      ctx.translate(pRef.current.x, pRef.current.y);
      ctx.scale(zRef.current, zRef.current);
    }

    if (forceBg && posterRef.current) {
      const pr = posterRef.current;
      const sc = Math.max(W / pr.naturalWidth, H / pr.naturalHeight);
      const sw = pr.naturalWidth * sc;
      const sh = pr.naturalHeight * sc;
      ctx.save();
      ctx.filter = `brightness(${dimFactor})`;
      ctx.drawImage(pr, (W - sw) / 2, (H - sh) / 2, sw, sh);
      ctx.restore();
    } else if (forceBg && !posterRef.current && !url) {
      ctx.fillStyle = "#1a2030";
      ctx.fillRect(0, 0, W, H);
      ctx.font = `600 ${Math.max(14, W / 24)}px 'DM Sans',sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(122,132,153,.4)";
      ctx.fillText("📷", W / 2, H / 2);
    } else if (!forceBg && !transparentIfNoBg) {
      const tile = Math.max(8, W / 60);
      ctx.fillStyle = "#1a1f2e";
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#222838";
      for (let y = 0; y < H; y += tile) {
        for (let x = 0; x < W; x += tile) {
          if (((x / tile) + (y / tile)) % 2 === 0) ctx.fillRect(x, y, tile, tile);
        }
      }
    }

    if (forceLayers) {
      layers.forEach(layer => {
        const layerUrl = getImageUrl(layer);
        if (!layerUrl) return;
        const img = layerImagesRef.current[layerUrl];
        if (!img) return;

        const ratio = img.naturalHeight / img.naturalWidth;
        const lW = Math.round(W * ((layer.size ?? 25) / 100));
        const lH = Math.round(ratio * lW);
        const cx = ((layer.pos?.x ?? 50) / 100) * W;
        const cy = ((layer.pos?.y ?? 50) / 100) * H;

        ctx.save();
        ctx.globalAlpha = (layer.opacity ?? 100) / 100;
        ctx.translate(cx, cy);
        ctx.rotate(((layer.rotation ?? 0) * Math.PI) / 180);
        ctx.shadowColor = "rgba(0,0,0,.35)";
        ctx.shadowBlur = 12 * (W / 540) / zRef.current;
        ctx.drawImage(img, -lW / 2, -lH / 2, lW, lH);
        ctx.restore();
      });
    }

    if (forceTexts) {
      texts.forEach(tb => {
        const fontDef = FONT_STYLES.find(f => f.id === tb.fontId) || FONT_STYLES[0];
        const fs = Math.round((tb.size ?? 56) * (W / 540));
        const tx = ((tb.pos?.x ?? 50) / 100) * W;
        const ty = ((tb.pos?.y ?? 35) / 100) * H;

        const applyCase = s =>
          tb.textTransform === "uppercase" ? s.toUpperCase()
          : tb.textTransform === "lowercase" ? s.toLowerCase()
          : s;
        const line = applyCase(tb.content || "");

        ctx.save();
        ctx.translate(tx, ty);
        ctx.rotate(((tb.rotation ?? 0) * Math.PI) / 180);
        ctx.globalAlpha = (tb.opacity ?? 100) / 100;
        ctx.font = `900 ${fs}px ${fontDef.css}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        if (tb.shadowEnabled) {
          ctx.shadowColor = tb.shadowColor || "#000";
          ctx.shadowBlur = fs * 0.12;
          ctx.shadowOffsetX = fs * 0.04;
          ctx.shadowOffsetY = fs * 0.04;
        }

        if (tb.gradient) {
          const grad = ctx.createLinearGradient(-W * 0.3, 0, W * 0.3, 0);
          grad.addColorStop(0, tb.color || "#c9a227");
          grad.addColorStop(1, tb.gradColor || "#007399");
          ctx.fillStyle = grad;
        } else {
          ctx.fillStyle = tb.color || "#c9a227";
        }

        ctx.fillText(line, 0, 0);

        if ((tb.outlineWidth ?? 0) > 0) {
          ctx.shadowBlur = 0;
          ctx.shadowColor = "transparent";
          ctx.strokeStyle = tb.outlineColor || "#000";
          ctx.lineWidth = (tb.outlineWidth ?? 0) * (W / 540);
          ctx.strokeText(line, 0, 0);
        }
        ctx.restore();
      });
    }

    if (applyTransform) ctx.restore();

    if (showLabel && !hideLabel && slot?.label) {
      const lh = Math.max(24, H * 0.08);
      const grad = ctx.createLinearGradient(0, H - lh, 0, H);
      grad.addColorStop(0, "rgba(0,0,0,0)");
      grad.addColorStop(1, "rgba(0,0,0,0.7)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, H - lh, W, lh);

      ctx.font = `600 ${Math.max(10, W / 50)}px 'DM Sans',sans-serif`;
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(slot.label, W / 2, H - lh / 2);
    }
  }, [showBg, showTexts, showLayers, layers, texts, slot, dimFactor, showLabel, url]);

  useEffect(() => {
    const fit = () => {
      const canvas = canvasRef.current;
      const stage = stageRef.current;
      if (!canvas || !stage) return;
      const W = stage.clientWidth || 300;
      const H = Math.round(W / aspectValue);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawTo(ctx, W, H);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [drawTo, aspectValue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const ctx = canvas.getContext("2d");
    drawTo(ctx, W, H);
  }, [tick, mode, drawTo]);

  useEffect(() => {
    if (!enablePanInteraction || !onZoomChange) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toLocal = (e) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const t = e.touches?.[0] ?? e;
      return {
        x: (t.clientX - rect.left) * (canvas.width / dpr / rect.width),
        y: (t.clientY - rect.top)  * (canvas.height / dpr / rect.height)
      };
    };

    const clampPan = (px, py, z) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = canvas.width / dpr;
      const H = canvas.height / dpr;
      return {
        x: Math.min(0, Math.max(-W * (z - 1), px)),
        y: Math.min(0, Math.max(-H * (z - 1), py))
      };
    };

    const onMouseDown = (e) => {
      if (zRef.current <= 1) return;
      e.preventDefault();
      const { x, y } = toLocal(e);
      panStart.current = { mx: x, my: y, px: pRef.current.x, py: pRef.current.y };
      panning.current = true;
      canvas.style.cursor = "grabbing";
    };

    const onMouseMove = (e) => {
      if (!panning.current) return;
      e.preventDefault();
      const { x, y } = toLocal(e);
      const newPan = clampPan(
        panStart.current.px + (x - panStart.current.mx),
        panStart.current.py + (y - panStart.current.my),
        zRef.current
      );
      pRef.current = newPan;
      onZoomChange?.(zRef.current, newPan);
    };

    const onMouseUp = () => {
      panning.current = false;
      canvas.style.cursor = zRef.current > 1 ? "grab" : "default";
    };

    const onWheel = (e) => {
      e.preventDefault();
      const { x, y } = toLocal(e);
      const direction = e.deltaY > 0 ? -1 : 1;
      const newZ = Math.min(5, Math.max(1, zRef.current + direction * 0.15 * zRef.current));
      const newPan = clampPan(
        x - (x - pRef.current.x) * (newZ / zRef.current),
        y - (y - pRef.current.y) * (newZ / zRef.current),
        newZ
      );
      onZoomChange?.(newZ, newPan);
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("wheel",     onWheel, { passive: false });
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);

    canvas.style.cursor = zRef.current > 1 ? "grab" : "default";

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("wheel",     onWheel);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  }, [enablePanInteraction, onZoomChange, zoom]);

  const handleDownload = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (downloading) return;

    setDownloading(true);
    try {
      const HD_W = downloadHD ? 1080 : (canvasRef.current?.clientWidth || 540);
      const HD_H = Math.round(HD_W / aspectValue);

      const tmp = document.createElement("canvas");
      tmp.width = HD_W;
      tmp.height = HD_H;
      const ctx = tmp.getContext("2d");

      const transparentExport = mode === "elements";

      drawTo(ctx, HD_W, HD_H, {
        hideLabel: true,
        transparentIfNoBg: transparentExport,
        applyTransform: false
      });

      const dataUrl = tmp.toDataURL("image/png", 1.0);
      const safeName = (downloadFilename || slot?.label || "preview")
        .toString().toLowerCase()
        .replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const modeSuffix = mode === "all" ? "full"
                       : mode === "url" ? "background"
                       : "elements";
      const link = document.createElement("a");
      link.download = `elakay-${safeName || "preview"}-${modeSuffix}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
      alert(__elakayText("t104", "Download failed. Possible CORS issue with images."));
    } finally {
      setDownloading(false);
    }
  };

const hasBg = !!url;
const hasElements = texts.length > 0 || layers.length > 0;

const MODES = useMemo(() => [
  {
    id: "all",
    label: __elakayText("t105", "All"),
    icon: "🎨",
    title: __elakayText("t066", "Show everything"),
    disabled: !hasBg && !hasElements
  },
  {
    id: "url",
    label: __elakayText("t106", "Background"),
    icon: "🖼️",
    title: __elakayText("t067", "Show background only"),
    disabled: !hasBg
  },
  {
    id: "elements",
    label: __elakayText("t107", "Elements"),
    icon: "✏️",
    title: __elakayText("t068", "Show texts and image overlays"),
    disabled: !hasElements
  }
], [hasBg, hasElements]);

useEffect(() => {
  const current = MODES.find(m => m.id === mode);

  if (current?.disabled) {
    const firstAvailable = MODES.find(m => !m.disabled);
    if (firstAvailable && firstAvailable.id !== mode) {
      setMode(firstAvailable.id);
    }
  }
}, [MODES, mode]);

  return (
    <div ref={stageRef}
      className={className}
      style={{
        position: "relative",
        aspectRatio,
        overflow: "hidden",
        background: "var(--n4)",
        ...style
      }}>

      <canvas ref={canvasRef}
        style={{ display: "block", width: "100%", height: "100%" }}/>

      {showControls && (
        <div style={{
          position: "absolute",
          bottom: 35, left: 8, right: 8, zIndex: 10,
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"
        }}>
          <div style={{
            display: "flex", gap: 2, padding: 3,
            background: "rgba(10,15,28,.85)",
            borderRadius: 18,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            boxShadow: "0 2px 10px rgba(0,0,0,.3)",
            border: "1px solid rgba(255,255,255,.1)"
          }}>
            {MODES.map(m => (
              <button key={m.id} type="button"
                onClick={e => { e.stopPropagation(); e.preventDefault(); if (!m.disabled) setMode(m.id); }}
                disabled={m.disabled}
                title={m.title}
                style={{
                  padding: "5px 10px", borderRadius: 14, border: "none",
                  background: mode === m.id
                    ? "linear-gradient(135deg, #007399, #00a4d8)"
                    : "transparent",
                  color: mode === m.id ? "#fff" : "rgba(255,255,255,.65)",
                  fontSize: 10, fontWeight: 700, fontFamily: "var(--ffb)",
                  cursor: m.disabled ? "not-allowed" : "pointer",
                  opacity: m.disabled ? 0.35 : 1,
                  transition: "all .15s",
                  display: "flex", alignItems: "center", gap: 4,
                  letterSpacing: 0.3, textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  boxShadow: mode === m.id ? "0 2px 8px rgba(0,115,153,.4)" : "none"
                }}>
                <span>{m.icon}</span>
                <span>{m.label}</span>
              </button>
            ))}
          </div>

          <div style={{ flex: 1, minWidth: 4 }}/>

          <button type="button"
            onClick={handleDownload}
            disabled={downloading}
            title={elakayAutoText("h_fcc503b9bf731b", "Download {value0} as PNG", { value0: mode === "all" ? "complete design"
                  : mode === "url" ? "background image"
                  : "texts and overlays (transparent)" })}
            style={{
              width: 36, height: 30, borderRadius: 15, border: "none",
              background: downloading
                ? "rgba(0,115,153,.5)"
                : "linear-gradient(135deg, #34c759, #4ade80)",
              color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: downloading ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              boxShadow: "0 2px 10px rgba(52,199,89,.35)",
              transition: "all .15s",
              opacity: downloading ? 0.7 : 1
            }}>
            {downloading ? "⏳" : "↓"}
          </button>
        </div>
      )}

      {showControls && (
        <div style={{
          position: "absolute", bottom: 8, right: 8, zIndex: 10,
          padding: "2px 8px",
          background: "rgba(10,15,28,.7)",
          borderRadius: 8,
          fontSize: 8.5, fontWeight: 600,
          color: "rgba(255,255,255,.65)",
          fontFamily: "var(--ffm)",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          backdropFilter: "blur(4px)",
          pointerEvents: "none"
        }}>{__elakayText("t069", "Mode:")}{mode} {zoom > 1 && `· ${Math.round(zoom * 100)}%`}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Composants atomiques
══════════════════════════════════════════════════════ */
function Pill({ icon, text, color }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", background: "rgba(0,115,153,.05)",
      border: "1px solid var(--b2)", borderRadius: 12,
      fontSize: 10, color, fontWeight: 600
    }}>
      <span>{icon}</span>{text}
    </span>
  );
}

function PropItem({ label, value }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 1,
      padding: "4px 6px", background: "var(--n4)", borderRadius: 4
    }}>
      <span style={{ fontSize: 8, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </span>
      <span style={{ color: "var(--white)", fontWeight: 600, fontSize: 10 }}>{value}</span>
    </div>
  );
}

function DetailSection({ title, icon, count = null, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 700, color: "var(--muted)",
        textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8,
        display: "flex", alignItems: "center", gap: 6
      }}>
        <span style={{ fontSize: 12 }}>{icon}</span>
        <span>{title}</span>
        {count !== null && (
          <span style={{
            fontSize: 9, padding: "1px 7px", borderRadius: 10,
            background: "var(--n3)", color: "var(--gold)",
            fontFamily: "var(--ffm)", fontWeight: 700
          }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function TextBlockRow({ tb, index }) {
  return (
    <div style={{
      background: "var(--n3)", border: "1px solid var(--b)", borderRadius: "var(--r)",
      padding: 12, display: "flex", gap: 12, alignItems: "flex-start"
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: tb.color,
        border: "2px solid rgba(255,255,255,.15)", flexShrink: 0,
        boxShadow: tb.gradient ? `inset 0 -10px 0 ${tb.gradColor}` : "none"
      }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 9, padding: "2px 7px", borderRadius: 10,
            background: "var(--n4)", color: "var(--muted)",
            fontFamily: "var(--ffm)", fontWeight: 700
          }}>#{index + 1}</span>
          <span style={{
            fontSize: 11, color: "var(--gold)", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.6
          }}>{tb.role}</span>
        </div>
        <div style={{
          fontSize: 18, fontFamily: "Impact, sans-serif", color: tb.color,
          letterSpacing: 1, marginBottom: 6,
          textShadow: tb.shadowEnabled ? `2px 2px 0 ${tb.shadowColor}` : "none",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
        }}>
          {tb.textTransform === "uppercase" ? (tb.content || "").toUpperCase() : tb.content}
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
          gap: 6, fontSize: 9.5, fontFamily: "var(--ffm)"
        }}>
          <PropItem label={__elakayText("t108", "Size")} value={`${tb.size}px`} />
          <PropItem label={__elakayText("t109", "Font")} value={tb.fontId} />
          <PropItem label={__elakayText("t110", "Rotation")} value={`${tb.rotation}°`} />
          <PropItem label={__elakayText("t111", "Opacity")} value={`${tb.opacity}%`} />
          <PropItem label={__elakayText("t112", "Position")} value={`${Math.round(tb.pos.x)}%, ${Math.round(tb.pos.y)}%`} />
          <PropItem label={__elakayText("t113", "Case")} value={tb.textTransform} />
          {tb.outlineWidth > 0 && <PropItem label={__elakayText("t114", "Outline")} value={`${tb.outlineWidth}px`} />}
          {tb.gradient && <PropItem label={__elakayText("t115", "Effect")} value="🌈 Gradient" />}
          {tb.shadowEnabled && <PropItem label={__elakayText("t115", "Effect")} value="🌒 Shadow" />}
        </div>
      </div>
    </div>
  );
}

function LayerRow({ layer, index }) {
  const isNew = layer.file instanceof File;
  const url = toImageUrl(layer.urlImg || layer.url || layer.src || "");

  return (
    <div style={{
      background: "var(--n3)",
      border: `1px solid ${isNew ? "rgba(0,115,153,.3)" : "var(--b)"}`,
      borderRadius: "var(--r)", padding: 12,
      display: "flex", gap: 12, alignItems: "flex-start"
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 6,
        overflow: "hidden", background: "var(--n4)",
        flexShrink: 0, position: "relative",
        border: "1px solid var(--b)"
      }}>
        {url ? (
          <img src={url} alt={elakayAutoText("h_b100a9538f8021", "Layer {value0}", { value0: index + 1 })}
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
            onError={e => { e.target.style.display = "none"; }}/>
        ) : (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--muted2)", fontSize: 22
          }}>🖼️</div>
        )}
        {isNew && (
          <div style={{
            position: "absolute", top: 2, right: 2,
            padding: "1px 5px", background: "var(--gold)",
            borderRadius: 4, fontSize: 7, fontWeight: 700, color: "#fff"
          }}>{elakayAutoText("h_66aabd91fc41cc", "NEW")}</div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 9, padding: "2px 7px", borderRadius: 10,
            background: "var(--n4)", color: "var(--muted)",
            fontFamily: "var(--ffm)", fontWeight: 700
          }}>#{index + 1}</span>
          <span style={{
            fontSize: 11, color: "var(--teal)", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.6
          }}>{__elakayText("t070", "Image overlay")}</span>
        </div>

        {url && (
          <div style={{
            fontSize: 9, color: "var(--muted)",
            fontFamily: "var(--ffm)", marginBottom: 8,
            wordBreak: "break-all", opacity: 0.8,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: "100%"
          }}>{url}</div>
        )}

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: 6, fontSize: 9.5, fontFamily: "var(--ffm)"
        }}>
          <PropItem label={__elakayText("t108", "Size")} value={`${layer.size}%`} />
          <PropItem label={__elakayText("t110", "Rotation")} value={`${layer.rotation}°`} />
          <PropItem label={__elakayText("t111", "Opacity")} value={`${layer.opacity}%`} />
          <PropItem label={__elakayText("t112", "Position")} value={`${Math.round(layer.pos.x)}%, ${Math.round(layer.pos.y)}%`} />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ReadOnlyFaceDetails — Détails d'une face en lecture seule
══════════════════════════════════════════════════════ */
function ReadOnlyFaceDetails({ slot, designSlot, index }) {
  const textsCount  = designSlot.texts.length;
  const layersCount = designSlot.layers.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        borderRadius: "var(--r)", overflow: "hidden",
        border: "1px solid var(--b)", background: "var(--n3)"
      }}>
        <SlotPreview
          slot={{ ...slot, slotState: designSlot }}
          aspectRatio="3/4"
          dimFactor={0.7}
        />

        <div style={{ padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)" }}>{__elakayText("t035", "Face #")}{index + 1} — {slot.label}
          </div>
          <div style={{
            fontSize: 9, color: "var(--muted)",
            fontFamily: "var(--ffm)", marginTop: 2,
            wordBreak: "break-all"
          }}>{__elakayText("t036", "ID:")}{slot.id}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <Pill icon="✏️" text={`${textsCount} text${textsCount > 1 ? "s" : ""}`} color="var(--gold)" />
            <Pill icon="🖼️" text={`${layersCount} image${layersCount > 1 ? "s" : ""}`} color="var(--teal)" />
          </div>
        </div>
      </div>

      {textsCount > 0 && (
        <DetailSection title={__elakayText("t037", "Text blocks")} icon="✏️" count={textsCount}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {designSlot.texts.map((tb, i) => <TextBlockRow key={i} tb={tb} index={i} />)}
          </div>
        </DetailSection>
      )}

      {layersCount > 0 && (
        <DetailSection title={__elakayText("t038", "Image overlays")} icon="🖼️" count={layersCount}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {designSlot.layers.map((layer, i) => <LayerRow key={i} layer={layer} index={i} />)}
          </div>
        </DetailSection>
      )}

      {designSlot.instructions && (
        <DetailSection title={__elakayText("t011", "Instructions")} icon="📝">
          <div style={{
            padding: "10px 12px",
            background: "rgba(0,115,153,.06)",
            border: "1px solid rgba(0,115,153,.2)",
            borderRadius: "var(--r)",
            fontSize: 11, color: "var(--gold2)",
            lineHeight: 1.5, fontStyle: "italic"
          }}>
            "{designSlot.instructions}"
          </div>
        </DetailSection>
      )}

      {textsCount === 0 && layersCount === 0 && !designSlot.instructions && (
        <div style={{
          padding: 30, textAlign: "center",
          color: "var(--muted)", fontSize: 11, fontStyle: "italic",
          background: "var(--n3)",
          border: "1px dashed var(--b2)",
          borderRadius: "var(--r)"
        }}>{__elakayText("t039", "No element on this face")}</div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   DesignCard_ — Carte d'un design dans la liste
══════════════════════════════════════════════════════ */
function DesignCard({
  design,
  isDeleting,
  onPreview,
  onEdit,
  onDelete,
  extraActions,
  showEditButton,

  showTop = true,
  topOptions = {
    status: true,
    faceCount: true,
    counters: true
  },

  showBottom = true,
  bottomOptions = {
    info: true,
    miniFaces: true,
    updatedAt: true,
    brightness: true,
    zoom: true,
    actions: true
  },

  selectable = true,
  selected = false,
  onSelect = null
}) {
  const isPublished = design.status === "published";
  const isDraft     = design.status === "draft";
  const isArchived  = design.status === "archived";

  const facesCount  = design.slots?.length || 0;
  const textsCount  = (design.slots || []).reduce((a, s) => a + (s.slotState?.texts?.length || 0), 0);
  const layersCount = (design.slots || []).reduce((a, s) => a + (s.slotState?.layers?.length || 0), 0);

  const thumbSlot = (design.slots || []).find(s => getImageUrl(s)) || design.slots?.[0];

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dimFactor, setDimFactor] = useState(1);

  const clampZoom = (z) => Math.min(5, Math.max(1, z));

  const stepZoom = (dir) => {
    const newZ = clampZoom(zoom + dir * 0.3 * zoom);
    if (newZ === 1) setPan({ x: 0, y: 0 });
    setZoom(newZ);
  };

  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleZoomChange = useCallback((z, p) => { setZoom(z); setPan(p); }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const statusBg = isPublished ? "rgba(52,199,89,.95)"
                : isDraft     ? "rgba(0,180,216,.95)"
                : isArchived  ? "rgba(122,132,153,.95)"
                              : "rgba(0,115,153,.95)";

  const borderColor = isPublished ? "rgba(52,199,89,.25)"
                    : isDraft     ? "rgba(0,180,216,.25)"
                    : isArchived  ? "rgba(122,132,153,.25)"
                                  : "var(--b2)";

  const DIM_PRESETS = [
    { val: 1, label: "100%" }, { val: 0.7, label: "70%" },
    { val: 0.4, label: "40%" }, { val: 0.15, label: "15%" },
    { val: 0.001, label: "0%" }
  ];

  return (
    <div style={{
      background: "var(--n2)",
      border: `1.5px solid ${borderColor}`,
      borderRadius: "var(--rL)",
      overflow: "hidden",
      display: "flex", flexDirection: "column",
      transition: "all .2s",
      opacity: isDeleting ? 0.5 : (isArchived ? 0.7 : 1)
    }}
    onMouseEnter={e => {
      if (isDeleting) return;
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,.15)";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}>

     {showTop && <div style={{ position: "relative" }}>
        <SlotPreview
       
          slot={thumbSlot}
          aspectRatio="9/9"
          showLabel={true}
          showControls={true}
          dimFactor={dimFactor}
          zoom={zoom}
          pan={pan}
          onZoomChange={handleZoomChange}
        />
       

       {topOptions.status && (   <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 5,
          padding: "4px 10px", borderRadius: 14,
          background: statusBg,
          color: "#fff", fontSize: 9.5, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: 0.6,
          display: "flex", alignItems: "center", gap: 5,
          backdropFilter: "blur(4px)"
        }}>
          <span>●</span>{design.status}
        </div> 
      )}

         {topOptions.faceCount && (  <div style={{
          position: "absolute", top: 10, right: 10, zIndex: 5,
          padding: "4px 10px", borderRadius: 14,
          background: "rgba(0,0,0,.7)",
          color: "#fff", fontSize: 10, fontWeight: 600,
          backdropFilter: "blur(4px)"
        }}>
          {facesCount}{__elakayText("t116", "face")}{facesCount > 1 ? "s" : ""}
        </div>
       )}

           {topOptions.counters && ( <div style={{
          position: "absolute", bottom: 10, left: 10, zIndex: 5,
          display: "flex", gap: 4
        }}>
          {textsCount > 0 && (
            <span style={{
              padding: "3px 7px", borderRadius: 10,
              background: "rgba(0,0,0,.7)",
              color: "#fff", fontSize: 9, fontWeight: 600,
              backdropFilter: "blur(4px)"
            }}>✏️ {textsCount}</span>
          )}
          {layersCount > 0 && (
            <span style={{
              padding: "3px 7px", borderRadius: 10,
              background: "rgba(0,180,216,.85)",
              color: "#fff", fontSize: 9, fontWeight: 600,
              backdropFilter: "blur(4px)"
            }}>🖼️ {layersCount}</span>
          )}
        </div>
      )}
      </div>}

     {showBottom &&  <div  onClick={(e) => e.stopPropagation()} style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>

      {bottomOptions.info && (    <div>

          <div style={{
            fontSize: 15, fontWeight: 700, color: "var(--white)",
            marginBottom: 4,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
          }}>
            {design.label || elakayAutoText("h_621521f9a87886", "Untitled")}
          </div>

          <div style={{
            fontSize: 10, color: "var(--muted)",
            display: "flex", gap: 8, flexWrap: "wrap"
          }}>
            <span>🎨 {design.style}</span>
            <span>·</span>
            <span>{design.detail}</span>
          </div>

        </div>)}

        {bottomOptions.miniFaces && facesCount > 1 && (
          <div style={{ display: "flex", gap: 4, overflow: "hidden" }}>
            {(design.slots || []).slice(1, 5).map((s, i) => (
              <div key={i} style={{
                width: 36, height: 36, borderRadius: 5,
                overflow: "hidden",
                border: "1px solid var(--b)",
                flexShrink: 0
              }}>
                <SlotPreview slot={s} aspectRatio="1/1" dimFactor={0.85} />
              </div>
            ))}
            {facesCount > 5 && (
              <div style={{
                width: 36, height: 36, borderRadius: 5,
                background: "var(--n3)", border: "1px solid var(--b)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, color: "var(--muted)", fontWeight: 700,
                flexShrink: 0
              }}>+{facesCount - 5}</div>
            )}
          </div>
        )}

        {bottomOptions.updatedAt && ( <div style={{
          fontSize: 9.5, color: "var(--muted)",
          paddingTop: 8, borderTop: "1px solid var(--b)",
          display: "flex", justifyContent: "space-between"
        }}>
          <span>{__elakayText("t071", "Updated:")}{formatDate(design.updatedAt)}</span>
          {design.usageCount > 0 && (
            <span style={{ color: "var(--gold)" }}>👁 {design.usageCount}</span>
          )}
        </div>
        )}

        {/* Brightness + Zoom controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
           {bottomOptions.brightness && (<div style={{
            padding: "8px 10px",
            background: "rgba(0,115,153,.04)",
            border: "1px solid rgba(0,115,153,.12)",
            borderRadius: "var(--r)",
            display: "flex", flexDirection: "column", gap: 6
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{
                fontSize: 9, fontWeight: 700, color: "var(--muted)",
                textTransform: "uppercase", letterSpacing: 0.7,
                display: "flex", alignItems: "center", gap: 5
              }}>
                <span style={{ fontSize: 11 }}>🌗</span>{__elakayText("t117", "Brightness")}</span>
              <span style={{
                fontSize: 9.5, fontFamily: "var(--ffm)",
                color: "var(--gold)", fontWeight: 700
              }}>{Math.round(dimFactor * 100)}%</span>
            </div>

            <input type="range"
              min={0.001} max={1} step={0.001}
              value={dimFactor}
              onChange={e => setDimFactor(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: "var(--gold)", height: 4, cursor: "pointer" }}/>

            <div style={{ display: "flex", gap: 3 }}>
              {DIM_PRESETS.map(p => {
                const active = Math.abs(dimFactor - p.val) < 0.01;
                return (
                  <button key={p.val} type="button"
                    onClick={() => setDimFactor(p.val)}
                    style={{
                      flex: 1, padding: "3px 0",
                      fontSize: 8.5, fontWeight: 700,
                      fontFamily: "var(--ffm)",
                      borderRadius: 4,
                      border: `1px solid ${active ? "var(--gold)" : "var(--b2)"}`,
                      background: active ? "var(--golda)" : "transparent",
                      color: active ? "var(--gold)" : "var(--muted)",
                      cursor: "pointer", transition: "all .12s"
                    }}>{p.label}</button>
                );
              })}
            </div>
          </div>)}

              {bottomOptions.zoom && (<div style={{
            padding: "8px 10px",
            background: "rgba(0,180,216,.04)",
            border: "1px solid rgba(0,180,216,.12)",
            borderRadius: "var(--r)",
            display: "flex", flexDirection: "column", gap: 6
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{
                fontSize: 9, fontWeight: 700, color: "var(--muted)",
                textTransform: "uppercase", letterSpacing: 0.7,
                display: "flex", alignItems: "center", gap: 5
              }}>
                <span style={{ fontSize: 11 }}>🔍</span>{__elakayText("t118", "Zoom")}</span>
              <span style={{
                fontSize: 9.5, fontFamily: "var(--ffm)",
                color: "var(--teal)", fontWeight: 700
              }}>{Math.round(zoom * 100)}%</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button type="button"
                onClick={() => stepZoom(-1)}
                disabled={zoom <= 1}
                title={__elakayText("t072", "Zoom out")}
                style={{
                  width: 26, height: 22, borderRadius: 4,
                  border: "1px solid var(--b2)",
                  background: zoom <= 1 ? "transparent" : "var(--n3)",
                  color: zoom <= 1 ? "var(--muted2)" : "var(--white)",
                  fontSize: 13, fontWeight: 700,
                  cursor: zoom <= 1 ? "not-allowed" : "pointer",
                  opacity: zoom <= 1 ? 0.4 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>−</button>

              <input type="range"
                min={1} max={5} step={0.05}
                value={zoom}
                onChange={e => {
                  const z = parseFloat(e.target.value);
                  if (z === 1) setPan({ x: 0, y: 0 });
                  setZoom(z);
                }}
                style={{ flex: 1, accentColor: "var(--teal)", height: 4, cursor: "pointer" }}/>

              <button type="button"
                onClick={() => stepZoom(1)}
                disabled={zoom >= 5}
                title={__elakayText("t073", "Zoom in")}
                style={{
                  width: 26, height: 22, borderRadius: 4,
                  border: "1px solid var(--b2)",
                  background: zoom >= 5 ? "transparent" : "var(--n3)",
                  color: zoom >= 5 ? "var(--muted2)" : "var(--white)",
                  fontSize: 13, fontWeight: 700,
                  cursor: zoom >= 5 ? "not-allowed" : "pointer",
                  opacity: zoom >= 5 ? 0.4 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>+</button>

              {zoom > 1 && (
                <button type="button"
                  onClick={resetZoom}
                  title={__elakayText("t074", "Reset zoom")}
                  style={{
                    padding: "0 6px", height: 22, borderRadius: 4,
                    border: "1px solid var(--teal)",
                    background: "rgba(0,180,216,.1)",
                    color: "var(--teal)",
                    fontSize: 10, fontWeight: 700,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center"
                  }}>↺</button>
              )}
            </div>

            {zoom > 1 && (
              <div style={{
                fontSize: 8.5, color: "var(--muted)",
                fontStyle: "italic", textAlign: "center",
                opacity: 0.7
              }}>{__elakayText("t075", "💡 Drag the image to pan")}</div>
            )}
          </div>)}

        </div>

        {/* Actions */}
         {bottomOptions.actions && ( <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
          <button type="button" className="sd-btn sd-btn-sec"
            style={{ flex: 1, padding: "7px 10px", fontSize: 10, justifyContent: "center", minWidth: 70 }}
            onClick={onPreview}
            disabled={isDeleting}>{__elakayText("t076", "👁️ Preview")}</button>

          {showEditButton && onEdit && (
            <button type="button" className="sd-btn sd-btn-teal"
              style={{ flex: 1, padding: "7px 10px", fontSize: 10, justifyContent: "center", minWidth: 70 }}
              onClick={onEdit}
              disabled={isDeleting}>{__elakayText("t077", "✏️ Edit")}</button>
          )}

          {extraActions && extraActions(design)}

          {onDelete && (
            <button type="button"
              style={{
                padding: "7px 10px",
                border: "1px solid rgba(239,68,68,.3)",
                background: "transparent",
                color: "var(--err)",
                borderRadius: "var(--r)",
                cursor: isDeleting ? "not-allowed" : "pointer",
                fontSize: 10, fontFamily: "var(--ffb)",
                fontWeight: 600
              }}
              onClick={onDelete}
              disabled={isDeleting}
              title={__elakayText("t078", "Delete design")}>
              {isDeleting ? "⏳" : "🗑"}
            </button>
          )}
        </div>)}

      </div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ConfirmDeleteModal
══════════════════════════════════════════════════════ */
function ConfirmDeleteModal({ design, onConfirm, onCancel, isDeleting }) {
  useEffect(() => {
    const original = document.body.style.overflow;
    const originalPadding = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = original;
      document.body.style.paddingRight = originalPadding;
    };
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !isDeleting) onCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel, isDeleting]);

  const modalContent = (
    <>
      <style>{`
        @keyframes cdm-fade  { from{opacity:0} to{opacity:1} }
        @keyframes cdm-slide { from{transform:translateY(-30px) scale(0.97);opacity:0} to{transform:translateY(0) scale(1);opacity:1} }
        .cdm-backdrop {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          width: 100vw; height: 100vh; z-index: 1050;
          background: rgba(3, 6, 14, 0.85);
          -webkit-backdrop-filter: blur(6px);
          backdrop-filter: blur(6px);
          animation: cdm-fade .15s linear;
        }
        .cdm-container {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          width: 100vw; height: 100vh; z-index: 1055;
          overflow-y: auto; overflow-x: hidden;
          display: flex; align-items: center; justify-content: center;
          padding: 20px; box-sizing: border-box; outline: 0;
        }
        .cdm-modal {
          position: relative; background: var(--n1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--rL); overflow: hidden;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
          animation: cdm-slide .25s cubic-bezier(.22,1,.36,1);
          width: 100%; max-width: 440px;
          max-height: calc(100vh - 40px); margin: auto;
          display: flex; flex-direction: column;
        }
      `}</style>

      <div className="cdm-backdrop" />

      <div className="cdm-container"
        onClick={e => { if (e.target === e.currentTarget && !isDeleting) onCancel(); }}>

        <div className="cdm-modal">
          <div style={{
            padding: "20px 24px",
            background: "linear-gradient(180deg, rgba(239,68,68,.08), transparent)",
            borderBottom: "1px solid var(--b)",
            flexShrink: 0
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: "rgba(239,68,68,.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22
              }}>⚠️</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)" }}>{__elakayText("t079", "Delete this design?")}</div>
                <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>{__elakayText("t080", "This action cannot be undone.")}</div>
              </div>
            </div>
          </div>

          <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>
            <div style={{
              background: "var(--n3)",
              border: "1px solid var(--b)",
              borderRadius: "var(--r)",
              padding: "12px 14px", marginBottom: 14
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>
                {design.label || elakayAutoText("h_621521f9a87886", "Untitled")}
              </div>
              <div style={{ fontSize: 10, color: "var(--muted)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{
                  padding: "2px 7px", borderRadius: 10,
                  background: design.status === "published" ? "rgba(52,199,89,.15)" : "rgba(0,180,216,.15)",
                  color: design.status === "published" ? "var(--ok)" : "var(--teal)",
                  fontWeight: 700, textTransform: "uppercase", fontSize: 9
                }}>{design.status}</span>
                <span>{design.slots?.length || 0}{__elakayText("t081", "face(s)")}</span>
                <span>·</span>
                <span>{design.style}</span>
              </div>
            </div>

            <div style={{
              fontSize: 11, color: "var(--muted)", lineHeight: 1.5,
              padding: "10px 12px",
              background: "rgba(239,68,68,.05)",
              border: "1px solid rgba(239,68,68,.15)",
              borderRadius: "var(--r)"
            }}>{__elakayText("t082", "🛑 Once deleted, all associated images, texts, and customizations will be permanently lost.")}</div>
          </div>

          <div style={{
            padding: "14px 24px",
            background: "var(--n2)",
            borderTop: "1px solid var(--b)",
            display: "flex", justifyContent: "flex-end", gap: 8,
            flexShrink: 0
          }}>
            <button type="button" className="sd-btn sd-btn-sec"
              onClick={onCancel} disabled={isDeleting}>{__elakayText("t119", "Cancel")}</button>
            <button type="button"
              style={{
                padding: "8px 18px",
                background: "linear-gradient(135deg, #dc2626, #ef4444)",
                color: "#fff", border: "none",
                borderRadius: "var(--r)",
                cursor: isDeleting ? "not-allowed" : "pointer",
                fontSize: 11, fontWeight: 600, fontFamily: "var(--ffb)",
                textTransform: "uppercase", letterSpacing: 0.3,
                boxShadow: "0 4px 14px rgba(239,68,68,.35)",
                opacity: isDeleting ? 0.6 : 1
              }}
              onClick={onConfirm} disabled={isDeleting}>
              {isDeleting ? elakayAutoText("h_4cd79bc14295a0", "⏳ Deleting...") : elakayAutoText("h_5da15963b34455", "🗑 Delete Permanently")}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

/* ══════════════════════════════════════════════════════
   DesignsList
══════════════════════════════════════════════════════ */
function DesignsList(props) {
  const {
  productID, authToken, mode = "seller",
  showCreateButton = true,
  showEditButton = true,
  showDeleteButton = true,
  onCreateNew, onLoadDesign, onPreviewDesign,
  extraActions
} = props
  const list = useDesignsList({ productID, authToken, mode });
  const [filter, setFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filteredDesigns = useMemo(() => {
    if (filter === "all") return list.designs;
    return list.designs.filter(d => d.status === filter);
  }, [list.designs, filter]);

  const stats = useMemo(() => ({
    total:     list.designs.length,
    draft:     list.designs.filter(d => d.status === "draft").length,
    published: list.designs.filter(d => d.status === "published").length,
    archived:  list.designs.filter(d => d.status === "archived").length,
  }), [list.designs]);

  const handleDelete = async (designId) => {
    const success = await list.deleteDesign(designId);
    if (success) setConfirmDelete(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Stats */}
      <div className="sd-stats">
        <div className="sd-stat">
          <span className="sd-stat-val">{stats.total}</span>
          <span className="sd-stat-lbl">{__elakayText("t083", "Total Designs")}</span>
        </div>
        <div className="sd-stat" style={{ borderColor: filter === "published" ? "var(--ok)" : "var(--b)" }}>
          <span className="sd-stat-val" style={{ color: "var(--ok)" }}>{stats.published}</span>
          <span className="sd-stat-lbl">{__elakayText("t120", "Published")}</span>
        </div>
        {mode === "seller" && (
          <div className="sd-stat" style={{ borderColor: filter === "draft" ? "var(--teal)" : "var(--b)" }}>
            <span className="sd-stat-val" style={{ color: "var(--teal)" }}>{stats.draft}</span>
            <span className="sd-stat-lbl">{__elakayText("t121", "Drafts")}</span>
          </div>
        )}
        {stats.archived > 0 && (
          <div className="sd-stat" style={{ borderColor: filter === "archived" ? "var(--muted)" : "var(--b)" }}>
            <span className="sd-stat-val" style={{ color: "var(--muted)" }}>{stats.archived}</span>
            <span className="sd-stat-lbl">{__elakayText("t122", "Archived")}</span>
          </div>
        )}
      </div>

      {/* Error banner */}
      {list.error && (
        <div style={{
          background: "rgba(239,68,68,.06)",
          border: "1px solid rgba(239,68,68,.25)",
          borderRadius: "var(--r)", padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1, fontSize: 11, color: "var(--err)" }}>
            <strong>{__elakayText("t084", "Failed to load designs:")}</strong> {list.error}
          </div>
          <button type="button" onClick={list.clearError}
            style={{
              background: "transparent", border: "none",
              color: "var(--err)", cursor: "pointer", fontSize: 14
            }}>✕</button>
          <button type="button" className="sd-btn sd-btn-sec"
            style={{ padding: "4px 10px", fontSize: 10 }}
            onClick={list.fetchDesigns}>{__elakayText("t085", "🔄 Retry")}</button>
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "12px 16px",
        background: "var(--n2)",
        border: "1px solid var(--b)",
        borderRadius: "var(--r)"
      }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[
            { id: "all",       label: __elakayText("t105", "All"),       count: stats.total },
            { id: "published", label: __elakayText("t120", "Published"), count: stats.published },
            ...(mode === "seller" ? [{ id: "draft", label: __elakayText("t121", "Drafts"), count: stats.draft }] : []),
            ...(stats.archived > 0 ? [{ id: "archived", label: __elakayText("t122", "Archived"), count: stats.archived }] : [])
          ].map(f => (
            <button key={f.id} type="button"
              className={`sd-chip${filter === f.id ? " on" : ""}`}
              onClick={() => setFilter(f.id)}>
              {f.label}
              <span style={{
                marginLeft: 6, padding: "1px 6px", borderRadius: 8,
                background: filter === f.id ? "rgba(255,255,255,.2)" : "var(--n4)",
                fontSize: 9, fontWeight: 700
              }}>{f.count}</span>
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }}/>

        <button type="button" className="sd-btn sd-btn-sec"
          style={{ padding: "6px 12px", fontSize: 10 }}
          onClick={list.fetchDesigns}
          disabled={list.loading}>
          {list.loading ? elakayAutoText("h_6b73e0f134b3de", "⏳ Loading...") : elakayAutoText("h_9a108a00e61219", "🔄 Refresh")}
        </button>

        {showCreateButton && onCreateNew && (
          <button type="button" className="sd-btn sd-btn-gold" onClick={onCreateNew}>{__elakayText("t086", "➕ Create New Design")}</button>
        )}
      </div>

      {/* Loading state */}
      {list.loading && list.designs.length === 0 && (
        <div style={{
          padding: 60, textAlign: "center",
          background: "var(--n2)", border: "1px solid var(--b)",
          borderRadius: "var(--rL)"
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--white)" }}>{__elakayText("t087", "Loading your designs...")}</div>
        </div>
      )}

      {/* Empty state */}
      {!list.loading && filteredDesigns.length === 0 && !list.error && (
        <div style={{
          padding: 60, textAlign: "center",
          background: "var(--n2)", border: "2px dashed var(--b2)",
          borderRadius: "var(--rL)"
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎨</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--white)", marginBottom: 6 }}>
            {list.designs.length === 0 ? elakayAutoText("h_172df3a78a9c73", "No designs yet") : `No ${filter} designs`}
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 20, lineHeight: 1.6 }}>
            {list.designs.length === 0
              ? elakayAutoText("h_42567fc51d9e56", "Get started by creating your first design.")
              : `Switch to a different filter or create a new design.`
            }
          </div>
          {showCreateButton && onCreateNew && (
            <button type="button" className="sd-btn sd-btn-gold" onClick={onCreateNew}>
              🚀 {list.designs.length === 0 ? elakayAutoText("h_a8f079552278fc", "Create Your First Design") : elakayAutoText("h_47d863063ad0a5", "Create New Design")}
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {filteredDesigns.length > 0 && (
        <>
          <div style={{
            fontSize: 10, color: "var(--muted)",
            display: "flex", alignItems: "center", gap: 8,
            paddingLeft: 4
          }}>
            <span>{__elakayText("t123", "Showing")}<strong style={{ color: "var(--white)" }}>{filteredDesigns.length}</strong>
              {filter !== "all" && <> {filter}</>}{__elakayText("t124", "design")}{filteredDesigns.length > 1 ? "s" : ""}
            </span>
            {filter !== "all" && stats.total > filteredDesigns.length && (
              <button type="button"
                onClick={() => setFilter("all")}
                style={{
                  background: "transparent", border: "none",
                  color: "var(--gold)", cursor: "pointer",
                  fontSize: 10, fontWeight: 600,
                  textDecoration: "underline"
                }}>{__elakayText("t088", "Show all (")}{stats.total})</button>
            )}
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16
          }}>
            {filteredDesigns.map(design => (
              <DesignCard
                key={design._id}
                design={design}
                isDeleting={list.deleting === design._id}
                onPreview={() => onPreviewDesign?.(design)}
                onEdit={onLoadDesign ? () => onLoadDesign(design) : null}
                onDelete={showDeleteButton ? () => setConfirmDelete(design) : null}
                extraActions={extraActions}
                showEditButton={showEditButton}
                {...props}
              />
            ))}
          </div>
        </>
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          design={confirmDelete}
          onConfirm={() => handleDelete(confirmDelete._id)}
          onCancel={() => setConfirmDelete(null)}
          isDeleting={list.deleting === confirmDelete._id}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ReadOnlyCanvas — Affichage canvas en lecture seule (preview)
══════════════════════════════════════════════════════ */
function ReadOnlyCanvas({
  design,
  slotDef,
  /* 🆕 Toutes les options de visibilité */
  showTexts = true,
  showLayers = true,
  showLabel = true,
  showControls = true,
  dimFactor = 1,
  defaultMode = "all"
}) {
  const [readOnlyState, setReadOnlyState] = useState({
    zoom: 1,
    pan: { x: 0, y: 0 }
  });

  const [loadedLayers, setLoadedLayers] = useState({});

  /* Charge les images des layers */
  useEffect(() => {
    const layers = design?.layers || [];
    layers.forEach((layer) => {
      const url = getImageUrl(layer);
      if (!url) return;
      if (loadedLayers[url]) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setLoadedLayers(prev => ({ ...prev, [url]: img }));
      };
      img.onerror = () => console.warn("ReadOnlyCanvas: layer failed:", url);
      img.src = url;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design?.layers]);

  const noOp = useCallback(() => {}, []);

  const handleZoomPan = useCallback((zoom, pan) => {
    setReadOnlyState({ zoom, pan });
  }, []);

  /* Injecte les imgEl chargés dans les layers */
  const designForCanvas = useMemo(() => {
    const layersWithImg = (design?.layers || []).map(layer => {
      const url = getImageUrl(layer);
      const cachedImg = url ? loadedLayers[url] : null;
      return cachedImg ? { ...layer, imgEl: cachedImg } : layer;
    });

    return {
      texts:  design?.texts  || [],
      layers: layersWithImg,
      zoom:   readOnlyState.zoom,
      pan:    readOnlyState.pan,
      instructions: design?.instructions || ""
    };
  }, [design, readOnlyState, loadedLayers]);

  return (
    <div style={{
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: 400,
      background: "var(--n3)",
      borderRadius: "var(--r)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}>

      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <DesignCanvas
          design={designForCanvas}
          slotDef={slotDef}
          onUpdateTextBlock={noOp}
          onUpdateLayer={noOp}
          onAddLayer={noOp}
          onSetActiveEl={noOp}
          activeEl={null}
          onZoomPan={handleZoomPan}
          readOnly={true}
          /* 🆕 Toutes les nouvelles props passées à DesignCanvas */
          showTexts={showTexts}
          showLayers={showLayers}
          showLabel={showLabel}
          showControls={showControls}
          dimFactor={dimFactor}
          defaultMode={defaultMode}
        />
      </div>

      {/* ─── Overlay "Preview Mode" en haut à gauche ─── */}
      <div style={{
        position: "absolute",
        top: 12, left: 12,
        padding: "4px 12px",
        background: "rgba(0,115,153,.92)",
        color: "#fff",
        borderRadius: 14,
        fontSize: 10, fontWeight: 700,
        letterSpacing: 0.6, textTransform: "uppercase",
        backdropFilter: "blur(6px)",
        pointerEvents: "none",
        zIndex: 100,
        boxShadow: "0 4px 12px rgba(0,0,0,.3)",
        display: "flex", alignItems: "center", gap: 6
      }}>
        <span>👁</span>
        <span>{__elakayText("t089", "Preview Mode")}</span>
      </div>

      {/* ─── Compteurs en haut à droite ─── */}
      <div style={{
        position: "absolute",
        top: 40, right: 12,
        display: "flex", gap: 4,
        zIndex: 100,
        pointerEvents: "none"
      }}>
        {(design?.texts?.length || 0) > 0 && (
          <span style={{
            padding: "3px 8px",
            background: "rgba(201,162,39,.92)",
            color: "#fff",
            borderRadius: 10,
            fontSize: 9.5, fontWeight: 700,
            backdropFilter: "blur(4px)",
            boxShadow: "0 2px 6px rgba(0,0,0,.2)"
          }}>
            ✏️ {design.texts.length}
          </span>
        )}
        {(design?.layers?.length || 0) > 0 && (
          <span style={{
            padding: "3px 8px",
            background: "rgba(0,180,216,.92)",
            color: "#fff",
            borderRadius: 10,
            fontSize: 9.5, fontWeight: 700,
            backdropFilter: "blur(4px)",
            boxShadow: "0 2px 6px rgba(0,0,0,.2)"
          }}>
            🖼️ {design.layers.length}
          </span>
        )}
      </div>

    </div>
  );
}

/* ══════════════════════════════════════════════════════
   DesignPreviewModal_
══════════════════════════════════════════════════════ */
function DesignPreviewModal({ design, onClose, onEdit, showEditButton = true, extraActions }) {
  const [activeFaceIdx, setActiveFaceIdx] = useState(0);

  useEffect(() => {
    const original = document.body.style.overflow;
    const originalPadding = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = original;
      document.body.style.paddingRight = originalPadding;
    };
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (!design) return null;

  const parsed = parseLoadedDesign(design);
  const slot = parsed.slots[activeFaceIdx];
  const designSlot = parsed.designs[activeFaceIdx];
  const isPublished = design.status === "published";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US");
  };

  const modalContent = (
    <>
      <style>{`
        @keyframes dpm-fade  { from{opacity:0} to{opacity:1} }
        @keyframes dpm-slide { from{transform:translateY(-50px) scale(0.97);opacity:0} to{transform:translateY(0) scale(1);opacity:1} }
        .dpm-backdrop {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          width: 100vw; height: 100vh; z-index: 1050;
          -webkit-backdrop-filter: blur(8px);
          backdrop-filter: blur(8px);
          animation: dpm-fade .15s linear;
        }
        .dpm-container {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          width: 100vw; height: 100vh; z-index: 1055;
          overflow-y: auto; overflow-x: hidden;
          display: flex; align-items: center; justify-content: center;
          padding: 16px; box-sizing: border-box; outline: 0;
        }
        .dpm-modal {
          position: relative; background: #0a0f1c;
          border: 1px solid rgba(0, 164, 216, 0.22);
          border-radius: 18px; overflow: hidden;
          display: flex; flex-direction: column;
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.7);
          animation: dpm-slide .25s cubic-bezier(.22,1,.36,1);
          width: 100%; max-width: 1100px;
          max-height: calc(100vh - 32px); margin: auto;
        }
        @media (min-width: 640px) {
          .dpm-container { padding: 24px; }
          .dpm-modal { max-height: calc(100vh - 48px); }
        }
        @media (min-width: 1024px) {
          .dpm-container { padding: 32px; }
          .dpm-modal { max-height: calc(100vh - 64px); }
        }
        .dpm-header {
          padding: 16px 24px;
          border-bottom: 1px solid rgba(0, 115, 153, 0.10);
          background: linear-gradient(180deg, rgba(0, 115, 153, 0.08), transparent);
          display: flex; align-items: center; gap: 14px;
          flex-wrap: wrap; flex-shrink: 0;
        }
        .dpm-header-icon {
          width: 42px; height: 42px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .dpm-header-info { flex: 1; min-width: 200px; }
        .dpm-header-title { font-size: 16px; font-weight: 700; color: #e6f0ff; }
        .dpm-header-meta {
          font-size: 10.5px; color: #8a93a8;
          display: flex; gap: 8px; margin-top: 2px; flex-wrap: wrap;
        }
        .dpm-status-badge {
          padding: 2px 7px; border-radius: 10px;
          font-weight: 700; text-transform: uppercase; font-size: 9px;
        }
        .dpm-close {
          width: 32px; height: 32px; border-radius: 8px;
          background: #1a2236;
          border: 1px solid rgba(0, 115, 153, 0.18);
          color: #8a93a8; cursor: pointer; font-size: 14px;
          display: flex; align-items: center; justify-content: center;
          padding: 0; flex-shrink: 0; transition: all .15s;
        }
        .dpm-close:hover {
          background: rgba(239, 68, 68, 0.14);
          border-color: rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }
        .dpm-body {
          flex: 1; display: grid;
          grid-template-columns: 1fr 320px;
          min-height: 0; overflow: hidden;
        }
        .dpm-body-left {
          display: flex; flex-direction: column;
          border-right: 1px solid rgba(0, 115, 153, 0.10);
          overflow: hidden; min-height: 400px;
        }
        .dpm-body-right {
          overflow-y: auto; background: #0d1424; padding: 14px;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 164, 216, 0.28) transparent;
        }
        .dpm-body-right::-webkit-scrollbar       { width: 5px; }
        .dpm-body-right::-webkit-scrollbar-track { background: transparent; }
        .dpm-body-right::-webkit-scrollbar-thumb {
          background: rgba(0, 164, 216, 0.28);
          border-radius: 3px;
        }
        @media (max-width: 768px) {
          .dpm-body { grid-template-columns: 1fr; }
          .dpm-body-left {
            border-right: none;
            border-bottom: 1px solid rgba(0, 115, 153, 0.10);
            min-height: 300px; max-height: 50vh;
          }
        }
        .dpm-footer {
          padding: 12px 24px; background: #0d1424;
          border-top: 1px solid rgba(0, 115, 153, 0.10);
          display: flex; justify-content: space-between;
          align-items: center; flex-wrap: wrap; gap: 10px;
          font-size: 10px; color: #8a93a8; flex-shrink: 0;
        }
        .dpm-footer strong { color: #e6f0ff; }
      `}</style>

      <div className="dpm-backdrop" />

      <div className="dpm-container"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

        <div className="dpm-modal">
          <div className="dpm-header">
            <div className="dpm-header-icon"
              style={{ background: isPublished ? "var(--emerald)" : "var(--teal)" }}>
              {isPublished ? "✅" : "💾"}
            </div>

            <div className="dpm-header-info">
              <div className="dpm-header-title">{design.label || elakayAutoText("h_621521f9a87886", "Untitled")}</div>
              <div className="dpm-header-meta">
                <span className="dpm-status-badge"
                  style={{
                    background: isPublished ? "rgba(52,199,89,.15)" : "rgba(0,180,216,.15)",
                    color: isPublished ? "var(--ok)" : "var(--teal)"
                  }}>
                  {design.status}
                </span>
                <span>🎨 {design.style}</span>
                <span>· {design.detail}</span>
                <span style={{ fontFamily: "var(--ffm)", fontSize: 9 }}>{__elakayText("t036", "ID:")}{design._id?.slice(-8)}
                </span>
              </div>
            </div>

            {showEditButton && onEdit && (
              <button type="button" className="sd-btn sd-btn-teal" onClick={onEdit}>{__elakayText("t090", "✏️ Edit Design")}</button>
            )}
             {extraActions && extraActions(design)}

            <button type="button" className="dpm-close" onClick={onClose}>✕</button>
          </div>

          <div className="dpm-body">
            <div className="dpm-body-left">
              <div className="sd-face-tabs">
                {parsed.slots.map((s, i) => (
                  <button key={s.id} type="button"
                    className={`sd-face-tab${activeFaceIdx === i ? " on" : ""}`}
                    onClick={() => setActiveFaceIdx(i)}>
                    {s.label}
                    {(parsed.designs[i].texts.length > 0 || parsed.designs[i].layers.length > 0) && (
                      <span className="sd-face-dot" />
                    )}
                  </button>
                ))}
                <div className="sd-face-thumbs">
                  {parsed.slots.map((s, i) => (
                    <div key={s.id} className={`sd-face-thumb${activeFaceIdx === i ? " on" : ""}`}
                      onClick={() => setActiveFaceIdx(i)}>
                      {s.url
                        ? <img src={s.url} alt={s.label} onError={e => { e.target.style.display = "none"; }} />
                        : <span style={{ fontSize: 9, color: "var(--muted)" }}>{s.label.slice(0, 2)}</span>
                      }
                    </div>
                  ))}
                </div>
              </div>

              <div className="sd-canvas-zone">
                <ReadOnlyCanvas
                  design={designSlot}
                  slotDef={slot}
                  showLabel={true}
                  dimFactor={1}
                />
              </div>
            </div>

            <div className="dpm-body-right">
              <ReadOnlyFaceDetails
                slot={slot}
                designSlot={designSlot}
                index={activeFaceIdx}
              />
            </div>
          </div>

          <div className="dpm-footer">
            <span>{__elakayText("t091", "Created:")}<strong>{formatDate(design.createdAt)}</strong></span>
            <span>{__elakayText("t071", "Updated:")}<strong>{formatDate(design.updatedAt)}</strong></span>
            {design.usageCount > 0 && (
              <span style={{ color: "var(--gold)" }}>{__elakayText("t092", "👁 Used")}{design.usageCount}{__elakayText("t125", "time")}{design.usageCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

/* ══════════════════════════════════════════════════════
   GraffitiPreview — Composant principal exporté par défaut
══════════════════════════════════════════════════════ */
export default function GraffitiPreview({
  productID,
  authToken,
  mode = "seller",
  title = "My Designs",
  subtitle = "Manage your saved designs. Click any design to preview, edit, or delete it.",
  showHeader = true,
  showCreateButton = true,
  showEditButton = true,
  showDeleteButton = true,
  onCreateNew,
  onLoadDesign,
  extraActions
}) {
  const [previewDesign, setPreviewDesign] = useState(null);

  const handlePreviewDesign = (design) => {
    setPreviewDesign(design);
  };

  const handleEditFromPreview = () => {
    if (!previewDesign) return;
    const designToEdit = previewDesign;
    setPreviewDesign(null);
    onLoadDesign?.(designToEdit);
  };

  return (
    <>
      {showHeader && (
        <div className="sd-sec-head">
          <div className="sd-sec-title">{title}</div>
          <div className="sd-sec-sub">{subtitle}</div>
        </div>
      )}

      <DesignsList
        productID={productID}
        authToken={authToken}
        mode={mode}
        showCreateButton={showCreateButton}
        showEditButton={showEditButton}
        showDeleteButton={showDeleteButton}
        onCreateNew={onCreateNew}
        onLoadDesign={onLoadDesign}
        onPreviewDesign={handlePreviewDesign}
        extraActions={extraActions}
      />

      {previewDesign && (
        <DesignPreviewModal
          design={previewDesign}
          onClose={() => setPreviewDesign(null)}
          onEdit={onLoadDesign ? handleEditFromPreview : null}
          showEditButton={showEditButton && !!onLoadDesign}
        />
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════
   Exports nommés (pour réutilisation avancée)
══════════════════════════════════════════════════════ */
export {
  DesignsList,
  DesignPreviewModal,
  DesignCard,
  ConfirmDeleteModal,
  SlotPreview,
  ReadOnlyCanvas,
  ReadOnlyFaceDetails,
  parseLoadedDesign,
  getImageUrl,
  OutputView,
  FONT_STYLES
};
